import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { HandCoins, Pen, FirstAid } from '@phosphor-icons/react';

import { ROUTES } from 'enrollment/app/routes';
import { InternalPage } from 'theSource/components';
import { BenefitDetailsDescriptionStyled } from './BenefitDetailsStyled';
import { BenefitDetailsCard } from './BenefitDetailsCard';
import { BottomNavigation } from 'enrollment/components';
import { PSBannerWrapper } from 'enrollment/components/Shared/Banner';
import { BenefitDto } from 'enrollment/queries/useBenefitCategoryQuery/types';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanYearId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { useBenefitCategoryQuery } from '../../queries';
import { useConfigurationQuery } from 'theSource/queries';
import {
  getOptionalPcpPlans,
  getRequiredPcpPlans,
  filteredBeneficiaries,
  getAdditionalPlanInformation,
} from '../Plans/PlanCard/utils';
import { GenericPagePreview } from '../Shared/PreviewContainers/GenericPagePreview';
import { GenericGroupingContainer } from '../Shared/PreviewContainers/GenericGroupingContainer';
import { BenefitDetailsCardListDto } from './types';
import { queryKeyGenerator } from 'theSource/utils';
import { ThemedHeader } from 'components/ThemedHeader';

export const BenefitDetails = () => {
  const { t } = useTranslation('pages.enrollmentBeneficiaries');
  const navigate = useNavigate();

  const [disableButton, setDisableButton] = useState(true);

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;
  const enrollmentContext = useSelector(selectEnrollmentContext);
  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearContextId =
    enrollmentContext === 'life_event' ? orgPlanYearContextId : 0;
  let requiredPcpPlans: BenefitDto[] | undefined = [];
  let optionalPcpPlans: BenefitDto[] | undefined = [];
  let pcpPlans: BenefitDto[] | undefined = [];
  let eSignRequiredPlans: BenefitDto[] | undefined = [];
  let additionalPlanInfo = false;

  const benefitDetailsCardList: BenefitDetailsCardListDto[] = [];

  const goToNextPage = () => {
    navigate(`/enrollment/${ROUTES.enrollment.review.path}`);
  };

  const handleBenefitDetailsCard = (details: BenefitDetailsCardListDto) => {
    switch (details.id) {
      case 'addYourBeneficiaries':
        navigate(`/enrollment/${ROUTES.enrollment.manageBeneficiaries.path}`);
        return null;
      case 'addYourE-Signature':
        navigate(`/enrollment/${ROUTES.enrollment.eSign.path}`);
        return null;
      case 'addYourProviders':
        navigate(`/enrollment/${ROUTES.enrollment.manageProviders.path}`);
        return null;
      case 'addYourEvidenceOfInsurability':
        return null;
      case 'verifyYourFamily':
        return null;
      case 'addYourAdditionalPlanInfo':
        return navigate(
          `/enrollment${ROUTES.enrollment.AdditionalPlanInfo.path}`
        );
      default:
        return null;
    }
  };

  const {
    data: benefitData,
    isLoading: isBenefitsLoading,
    isFetching: isBenefitsFetching,
  } = useBenefitCategoryQuery({
    key: queryKeyGenerator(
      `benefits-details-${enrollmentContext}`,
      Number(orgPlanYearContextId)
    ),
    enrollmentContextType: enrollmentContext,
    enabled: true,
    lumosApiHost: String(lumosApiHost),
    includeEligibleDependentsData: true,
    orgPlanYearId: orgPlanYearContextId,
  });

  const requireBeneficiaryPlans = filteredBeneficiaries(benefitData)?.filter(
    (plan: any) => plan !== null && plan !== undefined
  );

  const getEsignPlans = () => {
    return benefitData?.data?.benefits?.filter(
      (benefit: BenefitDto) =>
        benefit?.coverage?.esign_data !== null &&
        benefit?.coverage?.esign_data?.requires_esign
    );
  };

  const allocatedBenefitPlans = requireBeneficiaryPlans?.filter(
    (benefitPlan: any) => {
      return benefitPlan?.beneficiaries?.length > 0;
    }
  );

  eSignRequiredPlans = getEsignPlans();
  const eSignCompletedPlans = eSignRequiredPlans?.filter(
    (benefit: BenefitDto) => {
      return benefit?.coverage?.esign_data?.esigned;
    }
  );

  requiredPcpPlans = getRequiredPcpPlans(benefitData) || [];
  optionalPcpPlans = getOptionalPcpPlans(benefitData) || [];
  pcpPlans = [...requiredPcpPlans, ...(optionalPcpPlans || [])];
  const completedRequiredPcpPlans = requiredPcpPlans?.filter(
    (benefit: BenefitDto) => {
      return (
        benefit?.coverage?.pcp_members?.pcp_code &&
        benefit?.coverage?.pcp_members?.pcp_name
      );
    }
  );
  const completedPcpPlans = pcpPlans?.filter(
    (benefit: BenefitDto) => {
      return (
        benefit?.coverage?.pcp_members?.pcp_code &&
        benefit?.coverage?.pcp_members?.pcp_name
      );
    }
  );

  additionalPlanInfo = getAdditionalPlanInformation(benefitData);

  requireBeneficiaryPlans?.length &&
    benefitDetailsCardList.push({
      id: 'addYourBeneficiaries',
      title: t('addYourBeneficiariesTitle'),
      description: t('addYourBeneficiariesDescription'),
      icon: <HandCoins size={30} />,
      required: '*Required',
      status: `${allocatedBenefitPlans?.length}/${requireBeneficiaryPlans?.length}`,
    });

  eSignRequiredPlans?.length &&
    benefitDetailsCardList.push({
      id: 'addYourE-Signature',
      title: t('addYourE-SignatureTitle'),
      description: t('addYourE-SignatureDescription'),
      icon: <Pen size={30} />,
      required: '*Required',
      status: `${eSignCompletedPlans?.length}/${eSignRequiredPlans?.length}`,
    });

  pcpPlans &&
    pcpPlans?.length &&
    benefitDetailsCardList.push({
      id: 'addYourProviders',
      title: t('addYourProviders'),
      description: t('addYourProvidersDescription'),
      icon: <FirstAid size={30} />,
      required: requiredPcpPlans?.length
        ? '*Required'
        : '',
      status: `${completedPcpPlans.length}/${pcpPlans.length}`,
    });

  additionalPlanInfo &&
    benefitDetailsCardList.push({
      id: 'addYourAdditionalPlanInfo',
      title: t('addYourAdditionalPlanInfoTitle'),
      description: t('addYourAdditionalPlanInfoDescription'),
      icon: <Pen size={30} />,
      required: '',
      status: '',
    });

  useEffect(() => {
    let disableContinue = false;

    if (Boolean(requireBeneficiaryPlans)) {
      disableContinue = requireBeneficiaryPlans?.length !== allocatedBenefitPlans?.length;
    }
    
    if (Boolean(requiredPcpPlans)) {
      disableContinue = requiredPcpPlans?.length !== completedRequiredPcpPlans?.length;
    }

    setDisableButton(disableContinue);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requireBeneficiaryPlans, requiredPcpPlans]);

  if (isBenefitsLoading || isBenefitsFetching) {
    return (
      <GenericPagePreview
        title={t('title')}
        containerDataTestId="benefit-preview-page"
        titleDataTestId="benefit-preview-title-loading"
      >
        <GenericGroupingContainer
          id="benefit-loading"
          groupName=""
          loading={true}
          aria-label="Loading..."
        >
          Loading...
        </GenericGroupingContainer>
      </GenericPagePreview>
    );
  }

  return (
    <>
      <InternalPage
        blockClasses="pb-4 pt-2_5 px-4 pt-md-3_5 px-md-0 mt-md-5 mt-lg-0 pt-lg-4_5"
        testId="enrollment-family-details-page"
      >
        <ThemedHeader cssClass="mt-1 mt-lg-0">{t('title')}</ThemedHeader>
        <h2 className="headline-3">{t('description.heading')}</h2>
        <BenefitDetailsDescriptionStyled>
          {t('description.content')}
        </BenefitDetailsDescriptionStyled>
        <>
          {benefitDetailsCardList.length < 1 && (
            <PSBannerWrapper
              message={t('benefitDetailsCompleteLabel')}
              level="info"
              icon={true}
              extraClass="mb-4"
            />
          )}
        </>
        <BenefitDetailsCard
          benefitDetailsCardList={benefitDetailsCardList}
          handleBenefitDetailsCard={handleBenefitDetailsCard}
        />
        <BottomNavigation
          onClickEvent={() => goToNextPage()}
          disableButton={disableButton}
        />
      </InternalPage>
    </>
  );
};
